import { Paper, Typography } from "@mui/material";
import { Box, useMediaQuery } from "@mui/system";
import { settings } from "app";
import CustomFilter from "features/CustomFilter";
import React, { useEffect, useState } from "react";
import {
  Datagrid,
  List,
  ReferenceField,
  SimpleList,
  TextField,
  TextInput,
  useNotify,
  useStore,
} from "react-admin";
import { EmptyList } from "server/components";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import SecuredComponent from "server/security/SecuredComponent";
import BulkMarkButtons from "./BulkMarkButtons";
import permissions from "app/permissions";
import { administrationRequests } from "api/administrationApi";
import { useNotifyError } from "utils/useNotifyError";
import dataProvider from "server/dataProvider";
import { MobileListRow } from "features/MobileListRow";

const preparatoryFilters = [
  <TextInput
    label="Входящ номер"
    source="number"
    alwaysOn
  />,
];

const ListPreparatoryYear = () => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const STORE_KEY = "candidaciesPreparatoryYear.filters";

  const [choicesAll, setChoices] = useState([]);
  // filters drives the <List> and the gate (persisted via useStore);
  // campaignIds is the draft bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const [campaignIds, setCampaignIds] = useState(filters?.campaignId ?? null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await dataProvider.customRequest(candidateRequests.getCampaignsSelect(), null, "GET");

        setChoices(data);
        // Auto-select and show when exactly one active campaign exists.
        const activeCampaigns = data?.filter(x => x.isActiveRegistration === true);
        if (activeCampaigns.length === 1) {
          const id = activeCampaigns[0].id;
          setCampaignIds(id);
          setFilters({ campaignId: id });
        }
      } catch (e) {
        notifyError(e);
      }
    };
    fetchData();
  }, [notifyError, setFilters]);

  const searchParams = () => {
    if (campaignIds) {
      setFilters({ campaignId: campaignIds });
    } else {
      notify("Моля въведете кампания!", { type: "error" });
    }
  };

  return (
    <>
      <CustomFilter
        title="Списък кампании"
        filters={[
          {
            label: "Кампания",
            value: campaignIds,
            setValue: setCampaignIds,
            type: "autocomplete",
            multiple: false,
            choices: choicesAll,
          },
        ]}
        cardWidth="700px"
        buttonLabel="Провери"
        buttonOnClick={searchParams}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на кандидатури.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          filters={preparatoryFilters}
          title="Кандидатури за подготвителна година"
          sx={settings.style.blockStyle}
          filter={{ campaignId: filters.campaignId }}
          empty={<EmptyList />}
        >
          {isMobile ? (
            <SimpleList
              empty={<EmptyList />}
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <MobileListRow fields={[{ label: "Вх. номер", value: record.admissionNumber }]} />
              )}
              secondaryText={record => (
                <MobileListRow fields={[{ label: "Кандидатура номер", value: record.candidacyId }]} />
              )}
            />
          ) : (
            <>
              <Typography
                color={"primary"}
                p={1.2}
                sx={{ display: "flex", gap: 2 }}
              >
                <DoneOutlineIcon />
                Изберете кандидати за Подготвителна година
              </Typography>
              <Datagrid
                bulkActionButtons={
                  <SecuredComponent permission={permissions.canMarkCandidatesForPreparatoryYear}>
                    <BulkMarkButtons />
                  </SecuredComponent>
                }
                rowClick={false}
                sx={settings.style.firstExpandIconDatagrid}
              >
                <TextField
                  source="admissionNumber"
                  label="Вх. номер"
                  sortable={false}
                />
                <ReferenceField
                  source="countryId"
                  reference={administrationRequests.getCountriesSelect()}
                  emptyText="-"
                  label="Държава"
                >
                  <TextField source="nameBg" />
                </ReferenceField>
                <TextField
                  label="Име"
                  source="firstName"
                  sortable={false}
                />
                <TextField
                  label="Презиме"
                  source="secondName"
                  sortable={false}
                />
                <TextField
                  label="Фамилия"
                  source="lastName"
                  sortable={false}
                />
              </Datagrid>
            </>
          )}
        </List>
      )}
    </>
  );
};

export default ListPreparatoryYear;
